import React, { useEffect, useMemo, useState } from 'react';
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  useApolloClient,
} from '@apollo/client';
import {
  ComponentRendering,
  RouteData,
  withSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import AuthorComponent from 'components/r2/BlogAuthor';
import BlogCategories from 'components/r2/BlogCategories';
import Icons from 'components/atoms/Icons';
import { GET_CATEGORIES } from 'components/r2/graphql/GET_CATEGORIES';
import { getDeliveryKey, getDeliveryUrl } from '../../lib/util';
import Head from 'next/head';

interface TextField {
  value: string;
}

interface LinkField {
  value: {
    href: string;
  };
}

interface ImageField {
  value: {
    src?: string;
    alt?: string;
  };
}

interface Category {
  id: string;
  displayName: string;
  fields: {
    hashvalue: TextField;
    link: LinkField;
    Name: TextField;
    WordPressId: TextField;
  };
}

interface Author {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    'Author Image': ImageField;
    'Author Name': TextField;
    Email: TextField;
    hashvalue: TextField;
    link: LinkField;
    Phone: TextField;
    slug: TextField;
    Summary: TextField;
    'WordPress Id': TextField;
  };
}

interface RouteFields {
  Author?: Author;
  Categories?: Category[];
  Tags?: Category[];
  BodyRichText?: TextField;
  'Last Updated'?: TextField;
  TitleRawText?: TextField;
  'Blog Image'?: TextField;
  OpenGraphTitle?: TextField;
  OpenGraphDescription?: TextField;
  OpenGraphImageUrl?: ImageField;
  MetaTitle?: TextField;
  'Meta Description'?: TextField;
  'Page Title'?: TextField;
}

interface BlogPostDetailsProps extends Partial<ComponentRendering> {
  rendering: {
    uid: string;
  };
  sitecoreContext: {
    route?: RouteData & {
      fields: RouteFields;
    };
  };
}

const formatDateForPostDetails = (sitecoreDate: string): string => {
  if (!sitecoreDate) return '';

  if (/^\d{8}T\d{6}$/.test(sitecoreDate)) {
    const year = sitecoreDate.slice(0, 4);
    const month = sitecoreDate.slice(4, 6);
    const day = sitecoreDate.slice(6, 8);
    const hours = sitecoreDate.slice(9, 11);
    const minutes = sitecoreDate.slice(11, 13);
    const seconds = sitecoreDate.slice(13, 15);
    sitecoreDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  }
  const dateObj = new Date(sitecoreDate);
  if (isNaN(dateObj.getTime())) return '';

  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatDateForRelatedPosts = (sitecoreDate: string): string => {
  if (!sitecoreDate || sitecoreDate.length < 8) return '';
  const year = sitecoreDate.slice(0, 4);
  const month = sitecoreDate.slice(4, 6);
  const day = sitecoreDate.slice(6, 8);
  return `${month}/${day}/${year}`;
};

const HtmlWithCtaBtnClass: React.FC<{ html: string }> = ({ html }) => {
  const [processed, setProcessed] = React.useState(html);
  React.useEffect(() => {
    try {
      // Parsing HTML string
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc
        .querySelectorAll<HTMLAnchorElement>('.alignright.cta a')
        .forEach(a => {
          if (!a.classList.contains('btn-primary'))
            a.classList.add('btn-primary');

          if (a.href?.startsWith('http')) {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
          }
        });

      setProcessed(doc.body.innerHTML);
    } catch {
      // if parsing fails render original
      setProcessed(html);
    }
  }, [html]);
  return (
    <div
      className="content-paragraph overhauled"
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: processed }}
    />
  );
};

const DateComponent: React.FC<{ dateValue: string }> = ({ dateValue }) => (
  <div className="content-title">
    Last updated {formatDateForPostDetails(dateValue)}
  </div>
);

const BlogPostDetails = (props: BlogPostDetailsProps) => {
  const routeFields = props.sitecoreContext.route?.fields;
  const apolloClient = useApolloClient();
  const [shareUrl, setShareUrl] = useState('');
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState<Error | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setShareUrl(window.location.href);
    }
  }, []);

  if (!routeFields) return <div>No blog post data available</div>;

  const categories = routeFields?.Categories || [];
  const tags = routeFields?.Tags || [];

  const formatToGuid = (id: string): string =>
    `${id.slice(0, 8)}-${id.slice(8, 12)}-${id.slice(12, 16)}-${id.slice(16, 20)}-${id.slice(20)}`;

  const randomCategoryId = useMemo(() => {
    if (!categories.length) return null;
    const random = categories[Math.floor(Math.random() * categories.length)];
    const rawId = random?.id?.replace(/-/g, '').toUpperCase();
    return rawId ? formatToGuid(rawId) : null;
  }, [categories]);

  useEffect(() => {
    const fetchRelatedPosts = async () => {
      if (!randomCategoryId) return;

      setRelatedLoading(true);
      try {
        const { data } = await apolloClient.query({
          query: GET_CATEGORIES,
          variables: {
            path: 'C8FFD0C1-12B0-475A-9CD9-E69075A5E472',
            categoryId: randomCategoryId,
            authorId: 'null',
            pageSize: 4,
            after: null,
          },
          fetchPolicy: 'network-only',
        });

        setRelatedPosts(data?.search?.results || []);
      } catch (err: any) {
        setRelatedError(err);
      } finally {
        setRelatedLoading(false);
      }
    };

    fetchRelatedPosts();
  }, [randomCategoryId, apolloClient]);

  const shareTitle = encodeURIComponent(routeFields?.TitleRawText?.value || '');
  return (
    <div
      className="blog-details overhauled"
      id={`blog-post-details-${props.rendering?.uid}`}
    >
      <Head>
        <title>{routeFields?.['Page Title']?.value}</title>
        <meta name="title"
        content={routeFields?.MetaTitle?.value} 
        />
        <meta
          name="description"
          content={routeFields?.['Meta Description']?.value}
        />
        <meta name="twitter:card"
        content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content={routeFields?.OpenGraphTitle?.value}
        />
        <meta
          name="twitter:description"
          content={routeFields?.OpenGraphDescription?.value}
        />
        <meta
          name="twitter:image"
          content={
            routeFields?.OpenGraphImageUrl?.value?.src ||
            routeFields?.['Blog Image']?.value
          }
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content={routeFields?.OpenGraphTitle?.value}
        />
        <meta
          property="og:description"
          content={routeFields?.OpenGraphDescription?.value}
        />
        <meta
          property="og:image"
          content={routeFields?.OpenGraphImageUrl?.value?.src}
        />
        <meta property="og:url" content={shareUrl} />
      </Head>

      {routeFields['Blog Image']?.value && (
        <img
          src={routeFields['Blog Image'].value}
          alt={routeFields['Blog Image'].value || 'Blog image'}
          className="hero-img"
        />
      )}
      <div className="blog-container">
        <div className="content-wrapper general-content">
          {routeFields.TitleRawText && (
            <RichText
              tag="h1"
              className="heading-1"
              field={routeFields.TitleRawText}
            />
          )}
          {routeFields['Last Updated']?.value && (
            <DateComponent dateValue={routeFields['Last Updated'].value} />
          )}
          {routeFields.BodyRichText?.value && (
            <HtmlWithCtaBtnClass html={routeFields.BodyRichText.value} />
          )}
          <div className="divider" />
          <BlogCategories fields={{ Categories: categories, Tags: tags }} />
          {routeFields.Author && (
            <>
              <AuthorComponent
                fields={routeFields.Author.fields}
                id={routeFields.Author.id}
              />
              <div className="divider" />
            </>
          )}
          <div className="share-bar">
            <h4 className="share-bar-title">Share this story</h4>
            <div className="social-icons">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noreferrer"
                className="social-icon"
                aria-label="Share on Facebook"
              >
                <Icons id="facebook-round" />
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noreferrer"
                className="social-icon"
                aria-label="Share on LinkedIn"
              >
                <Icons id="linkedin-round" />
              </a>

              <a
                href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl.trim())}&media=${encodeURIComponent(routeFields['Blog Image']?.value || '')}&description=${encodeURIComponent(routeFields?.TitleRawText?.value || '')}`}
                target="_blank"
                rel="noreferrer"
                className="social-icon"
                aria-label="Share on Pinterest"
              >
                <Icons id="pinterest-round" />
              </a>

              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareTitle}`}
                target="_blank"
                rel="noreferrer"
                className="social-icon"
                aria-label="Share on Twitter"
              >
                <Icons id="twitter-x-round" />
              </a>
            </div>
          </div>

          <div className="divider-with-margin" />

          <div className="related-cards-wrapper overhauled">
            <h2>Related</h2>
            {relatedLoading && <p>Loading related posts...</p>}
            {relatedError && (
              <p className="text-red-600">Error loading related posts.</p>
            )}
            <div className="related-cards">
              {Array.isArray(relatedPosts) &&
                relatedPosts.map((article: any) => (
                  <a
                    href={article.path.replace(
                      /^\/sitecore\/content\/osfhealthcare\/home\/blog/,
                      '/blog'
                    )}
                    key={article.id}
                    className="article-card"
                  >
                    <div className="article-image">
                      <img
                        className="related-image"
                        src={article.BlogImage?.value}
                        alt={article.TitleRichText?.value || 'Related article'}
                      />
                    </div>
                    <div className="article-content">
                      <div className="article-date">
                        {formatDateForRelatedPosts(
                          article.LastUpdated?.value || ''
                        )}
                      </div>
                      <h3
                        className="osf-display-h3 article-title"
                        dangerouslySetInnerHTML={{
                          __html: article.TitleRichText?.value || '',
                        }}
                      />
                    </div>
                  </a>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Apollo setup
const deliveryKey = getDeliveryKey();
const endpoint = getDeliveryUrl();
const client = new ApolloClient({
  link: new HttpLink({
    uri: endpoint,
    headers: {
      sc_apikey: deliveryKey,
    },
  }),
  cache: new InMemoryCache(),
});

// Export wrapped component
const BlogPostDetailsWithSitecore = withSitecoreContext()(BlogPostDetails);

const WrappedBlogPostDetails = (props: any) => (
  <ApolloProvider client={client}>
    <BlogPostDetailsWithSitecore {...props} />
  </ApolloProvider>
);

export default WrappedBlogPostDetails;
